@if (!hidden()) {
  <div class="container">
    @if (label(); as label) {
      <onyx-input-label
        [label]="label"
        [hint]="hint()"
        [required]="required() && showRequired()"
        [link]="link()"
        [disabled]="disabled()"
        (click)="focus()"
      ></onyx-input-label>
    }

    @let showPlaceholder = !selectedValue() && !showValueIcon();
    @let showRibbonPlaceholder =
      ribbon() && (!transparentRibbon() || !showRibbonValue());
    @let hasClear =
      !disabled() && !ribbon() && selectedValue() && (optional() || multiple());

    <div
      data-testid="dropdown"
      class="dropdown size-{{ size() }}"
      [class.ribbon]="ribbon()"
      [class.clear]="hasClear && showClear()"
      [class.transparent]="transparentRibbon()"
      [class.transparent-colored-output]="transparentRibbonColoredOutput()"
      [class.attached]="attached()"
      [class.active]="!showValueIcon() ? selectedValue() : !!value()"
      [class.disabled]="disabled()"
      [class.invalid]="isInvalid()"
      [class.multiple]="multiple() && !showPlaceholder"
      [tabIndex]="disabled() ? -1 : 0"
      [onyxDropdownOptions]="options()"
      [onyxDropdownValue]="value()"
      [onyxDropdownCompareKey]="compareKey()"
      [onyxDropdownOptional]="optional()"
      [onyxDropdownMultiple]="multiple()"
      [onyxDropdownShowSelectAll]="showSelectAll()"
      [onyxDropdownDisabled]="disabled()"
      [(onyxDropdownAttached)]="attached"
      [onyxDropdownSearch]="search() || isSource(options())"
      [onyxDropdownSearchPlaceholder]="searchPlaceholder()"
      [onyxDropdownSearchKeys]="searchKeys()"
      [(onyxDropdownQuery)]="query"
      [onyxDropdownLimit]="limit()"
      [(onyxDropdownPage)]="page"
      [onyxDropdownTotalItems]="totalItems()"
      [onyxDropdownNotFoundMessage]="notFoundMessage()"
      [onyxDropdownShowAddOption]="showAddOption()"
      [onyxDropdownAddOptionMessage]="addOptionMessage()"
      [onyxDropdownTemplateRef]="templateRef()"
      [onyxDropdownSubheading]="subheading()"
      [onyxDropdownSubheadingTemplateRef]="subheadingTemplateRef()"
      [onyxDropdownOptionTemplateRef]="optionTemplateRef()"
      [onyxDropdownOptionSize]="optionSize()"
      [onyxDropdownTheme]="theme()"
      [onyxDropdownWidth]="overlayWidth()"
      [onyxDropdownMaxHeight]="maxHeight()"
      (onyxDropdownValueChange)="changeValue($event)"
      (onyxDropdownOptionsChange)="optionsChange.emit($event)"
      (onyxDropdownTotalItemsChange)="totalItemsChange.emit($event)"
      (onyxDropdownSelectedValueChange)="selectedValueChange.emit($event)"
      (onyxDropdownSelectedValuesChange)="selectedValuesChange.emit($event)"
      (onyxDropdownSelectedOptionChange)="selectedOptionChange.emit($event)"
      (onyxDropdownSelectedOptionsChange)="
        selectedOptionsChange.emit($event); selectedOptions.set($event.options)
      "
      (onyxDropdownSelectedOptionValueChange)="
        selectedOptionValueChange.emit($event)
      "
      (onyxDropdownSelectedOptionValuesChange)="
        selectedOptionValuesChange.emit($event)
      "
      (onyxDropdownAddOption)="addOption.emit($event)"
      (onyxDropdownFetchingChange)="fetching.set($event)"
      (focusout)="touch.emit()"
      #dropdownElement
    >
      @if (ribbon()) {
        <div class="ribbon-icon">
          <ng-content select="[ribbonIcon]"></ng-content>
        </div>
      }

      <div
        data-testid="output"
        class="output"
        [class.placeholder]="showPlaceholder"
        [class.f-body-m]="ribbon()"
        [class.f-medium]="ribbon()"
      >
        @if (isFetchingData() && !showRibbonPlaceholder) {
          {{ `${I18N}.fetchingData` | translate }}
        } @else if (showPlaceholder || showRibbonPlaceholder) {
          {{ placeholder() | translate }}
        } @else if (multiple()) {
          <div
            class="output-items"
            [onyxOverflowItems]="selectedOptions()"
            [onyxOverflowItemTemplateRef]="overflowItemTemplate"
            [onyxOverflowCounterTemplateRef]="overflowCounterTemplate"
          ></div>
        } @else if (showValueIcon()) {
          @let value = selectedValue()!.toString();
          @if (UtilityHelper.isOnyxIcon(value)) {
            <onyx-icon [name]="value" [size]="16"></onyx-icon>
          } @else if (UtilityHelper.isEmoji(value)) {
            <onyx-emoji [name]="value"></onyx-emoji>
          }
        } @else {
          {{ selectedName() | translate }}
        }
      </div>

      <div class="actions">
        <div class="hidden">
          @if (hasClear && showClear()) {
            <onyx-clear-button
              data-testid="clear-button"
              (click)="clearValue(); $event.stopPropagation()"
            ></onyx-clear-button>
          }
        </div>
      </div>

      @if (ribbonCount() != null) {
        <onyx-notification-number-label
          [value]="ribbonCount()!"
          color="blue"
        ></onyx-notification-number-label>
      } @else if (ribbonRightIcon(); as icon) {
        <onyx-icon class="ribbon-icon" [name]="icon" [size]="20"></onyx-icon>
      } @else {
        <onyx-icon
          class="arrow"
          name="arrow-alt-down-outlined"
          [size]="20"
        ></onyx-icon>
      }
    </div>

    <ng-content></ng-content>
  </div>

  @let control = formField() ?? formControl();
  @if (control && showErrors()) {
    <onyx-error [errors]="[control]" variant="control"></onyx-error>
  }
}

<ng-template let-item="item" let-index="index" #overflowItemTemplate>
  <onyx-tag
    [size]="size() === 32 ? 24 : 32"
    [color]="tagColor()"
    [icon]="item.leftIcon?.name ?? null"
    [flag]="item?.leftFlag ?? null"
    [disabled]="disabled()"
    [inactive]="true"
  >
    {{ item.name | translate }}
  </onyx-tag>
</ng-template>

<ng-template let-count="count" #overflowCounterTemplate>
  <onyx-tag
    [size]="size() === 32 ? 24 : 32"
    [color]="tagColor()"
    [disabled]="disabled()"
    [inactive]="true"
  >
    +{{ count }}
  </onyx-tag>
</ng-template>
